import { useEffect } from 'react';
import { toast } from 'sonner';
import type { UserLanguage } from '@xtreed/academy-shared';
import i18n from '@/i18n';
import { canChooseLanguage, pickLanguage } from '@/i18n/sync';
import { authService } from '@/services/auth.service';
import { useAuthStore } from '@/stores/auth';
import { usePWAStore } from '@/stores/pwa';

export async function syncUserLanguage(language: UserLanguage) {
  const { isAuthenticated, user } = useAuthStore.getState();
  if (!isAuthenticated || !user || user.language === language) return;

  try {
    await authService.updateLanguage(language);
    const current = useAuthStore.getState().user;
    if (current && usePWAStore.getState().language === language) {
      useAuthStore.getState().setUser({ ...current, language });
    }
  } catch {
    toast.error(i18n.t('language.saveError'));
  }
}

export function useEffectiveLanguage(): UserLanguage {
  const chosen = usePWAStore((state) => state.language);
  const tenant = usePWAStore((state) => state.tenantLanguage);
  const user = useAuthStore((state) => state.user?.language);
  const role = useAuthStore((state) => state.user?.role);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  return pickLanguage({ chosen, tenant, user, role, isAuthenticated });
}

export function useCanChooseLanguage(): boolean {
  const tenant = usePWAStore((state) => state.tenantLanguage);
  const role = useAuthStore((state) => state.user?.role);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  return canChooseLanguage({ tenant, role, isAuthenticated });
}

export function useLanguage() {
  const language = useEffectiveLanguage();
  const setLanguage = usePWAStore((state) => state.setLanguage);
  return { language, setLanguage };
}

export function useLanguageSync() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const userLanguage = useAuthStore((state) => state.user?.language);
  const chosen = usePWAStore((state) => state.language);
  const canChoose = useCanChooseLanguage();

  useEffect(() => {
    if (canChoose && isAuthenticated && chosen && userLanguage !== chosen) {
      void syncUserLanguage(chosen);
    }
  }, [canChoose, isAuthenticated, userLanguage, chosen]);
}
